import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { thanksgivingPromo, isPromoActive } from '../data/promo';
import { company } from '../data/content';
import { PromoCountdown } from './PromoCountdown';
import { ArrowRightIcon, PhoneIcon, PlusIcon, CheckIcon } from './icons';
const ease = [0.23, 1, 0.32, 1] as const;
const STORAGE_KEY = `promo-modal-seen-${thanksgivingPromo.id}`;
const OPEN_DELAY_MS = 6000;
const PROMO_IMAGE = 'https://cdn.magicpatterns.com/uploads/p6Fr8MHuwbp3KC6bhmKzLi/aj12.png';
const perks = ['Sealed deck around the entire pool perimeter', 'Protects against stains, cracks, and fading', 'Only with a plaster job booked by Nov 30'];
export function PromoModal() {
  const [open, setOpen] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!isPromoActive()) return;
    try {
      if (window.sessionStorage.getItem(STORAGE_KEY) === '1') return;
    } catch {
      // Storage unavailable — still show once.
    }
    const id = window.setTimeout(() => {
      setOpen(true);
      try {
        window.sessionStorage.setItem(STORAGE_KEY, '1');
      } catch {
        // Ignore storage errors.
      }
    }, OPEN_DELAY_MS);
    return () => window.clearTimeout(id);
  }, []);
  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);
  const close = () => setOpen(false);
  return <AnimatePresence data-id="element-568" data-mp="1ii0kd4tvyvhw">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease
    }} onClick={close} className="fixed inset-0 z-[80] flex items-end justify-center bg-ink/80 p-0 sm:items-center sm:p-6" data-id="element-569" data-mp="1neqvreuqx2wz">
          <motion.div role="dialog" aria-modal="true" aria-labelledby="promo-title" initial={{
        opacity: 0,
        y: 24,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 16,
        scale: 0.98
      }} transition={{
        duration: 0.26,
        ease
      }} onClick={event => event.stopPropagation()} className="relative grid max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-t-2xl bg-ink sm:rounded-2xl md:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]" data-id="element-570" data-mp="3fevayf6mnnj9">
            <div className="relative h-44 md:h-auto" data-id="element-571" data-mp="3pcszl3sefmce">
              <img src={PROMO_IMAGE} alt="Pool with a freshly sealed, bright deck around the perimeter" className="h-full w-full object-cover" data-id="element-572" data-mp="3pb84nd26a1e0" />
              <span className="absolute left-4 top-4 rounded-md bg-ember px-3 py-1.5 font-banner text-[11px] font-black uppercase tracking-[0.16em] text-white" data-id="element-573" data-mp="lp0s81zxvlh5">
                Thanksgiving special
              </span>
            </div>

            <div className="p-6 sm:p-8" data-id="element-574" data-mp="t78m9ekecbio">
              <p className="font-banner text-[12px] font-extrabold uppercase tracking-[0.24em] text-gold" data-id="element-575" data-mp="t809a0zxh0xc">
                Limited time · {thanksgivingPromo.deadlineLabel}
              </p>
              <h2 id="promo-title" className="mt-3 font-banner text-3xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-4xl" data-id="element-576" data-mp="136g1666s4e7w">
                <span className="text-gold" data-id="element-577" data-mp="1c51gztkv9f4z">Free</span> Deco Seal
                <span className="block text-xl text-white/90 sm:text-2xl" data-id="element-578" data-mp="1gwt7k440vkv4">
                  around your pool perimeter
                </span>
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-white/75" data-id="element-579" data-mp="1lvcua6qfx0oj">
                Book a plaster job before November 30 and we&apos;ll seal the deck around your
                entire pool at no charge — our thank-you to Houston families this Thanksgiving.
              </p>

              <ul className="mt-5 space-y-2" data-id="element-580" data-mp="2sk5wpedx6bxk">
                {perks.map(perk => <li key={perk} className="flex items-start gap-2.5 text-sm text-white" data-id="element-581" data-mp="2nlb81vt9tt5j">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-gold" data-id="element-582" data-mp="31e0u208suy2n" />
                    {perk}
                  </li>)}
              </ul>

              <div className="mt-6" data-id="element-583" data-mp="29ml9ebqszlf2">
                <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-white/60" data-id="element-584" data-mp="29nmlmjjk2dyd">
                  Offer ends in
                </p>
                <PromoCountdown tone="gold" size="lg" data-id="element-585" data-mp="24putbof00ptn" />
              </div>

              <div className="mt-7 flex flex-col gap-2.5 sm:flex-row" data-id="element-586" data-mp="2iouf8ob4mqm4">
                <a href="#contact" onClick={close} className="inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-gold px-5 py-3.5 font-banner text-[15px] font-black uppercase tracking-wide text-ink transition-[transform,background-color] duration-150 ease-out hover:-translate-y-0.5 hover:bg-[#FFE34D]" data-id="element-587" data-mp="2dn54l9rtgeyk">
                  Claim my free seal
                  <ArrowRightIcon className="h-4 w-4" data-id="element-588" data-mp="5hw8uulbo148" />
                </a>
                <a href={company.phoneHref} className="inline-flex items-center justify-center gap-2 rounded-md border border-white/25 px-5 py-3.5 text-sm font-semibold text-white transition-colors duration-150 ease-out hover:border-gold hover:text-gold" data-id="element-589" data-mp="3nn7t6duvvuj0">
                  <PhoneIcon className="h-4 w-4" data-id="element-590" data-mp="p395to2ejwiz" />
                  {company.phoneDisplay}
                </a>
              </div>
            </div>

            <button ref={closeRef} type="button" onClick={close} aria-label="Close promotion" className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-ink/70 text-white transition-colors duration-150 ease-out hover:bg-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold" data-id="element-591" data-mp="fer3v3idzjdr">
              <PlusIcon className="h-5 w-5 rotate-45" data-id="element-592" data-mp="1crfqvnstz3lg" />
            </button>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}